// utils/booking/turnstile.ts
//
// Cloudflare Turnstile for the public booking page. The widget runs on
// 369.dev for every booking page (embeds included — the widget lives inside
// the 369.dev iframe), so one platform-wide site key serves all workspaces.
//
// Nothing here touches `window`/`document` at import time: the loader takes
// them as parameters so it can be unit-tested in a plain Node environment and
// is trivially SSR-safe (callers only invoke it from the client).

export const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const DEFAULT_LOAD_TIMEOUT_MS = 15_000

export type TurnstileTheme = 'light' | 'dark' | 'auto'
/** Cloudflare's widget sizes: normal 300×65, flexible 100%-width (min 300), compact 150×140. */
export type TurnstileSize = 'normal' | 'flexible' | 'compact'

/**
 * The daisyUI themes (utils/boardAppearance SUPPORTED_THEMES) whose base
 * colours are dark. The booking page pins its theme via `data-theme`, so the
 * widget must follow THAT rather than Turnstile's `auto` (which follows the
 * visitor's OS and lands a light widget on a dark page, or vice versa).
 */
export const DARK_BOOKING_THEMES = new Set([
  'dark',
  'black',
  'synthwave',
  'halloween',
  'forest',
  'dracula',
  'night',
  'coffee',
  'dim',
  'business',
  'luxury',
  'sunset',
  'abyss',
])

/** Map the page's pinned daisyUI theme to Turnstile's light/dark widget. */
export function turnstileThemeFor(theme: string): 'light' | 'dark' {
  return DARK_BOOKING_THEMES.has(String(theme ?? '').trim().toLowerCase()) ? 'dark' : 'light'
}

/** The subset of Cloudflare's `window.turnstile` this page uses. */
export interface TurnstileApi {
  render(container: HTMLElement | string, options: TurnstileRenderParams): string | undefined
  reset(widgetId?: string): void
  remove(widgetId: string): void
}

/** Cloudflare's render parameters (the ones we send). */
export interface TurnstileRenderParams {
  'sitekey': string
  'theme': TurnstileTheme
  'size': TurnstileSize
  'appearance': 'always' | 'execute' | 'interaction-only'
  'callback': (token: string) => void
  'expired-callback': () => void
  'error-callback': (code?: string) => void
}

declare global {
  interface Window {
    /** Defined by Cloudflare's api.js once it has loaded. */
    turnstile?: TurnstileApi
  }
}

// Minimal structural views (the real `window`, `document` and an
// HTMLScriptElement are assignable; tests pass tiny fakes).
export interface TurnstileWindowLike {
  turnstile?: TurnstileApi
}

export interface TurnstileScriptLike {
  src: string
  async: boolean
  defer: boolean
  onload: ((event?: any) => any) | null
  onerror: ((event?: any, ...rest: any[]) => any) | null
}

export interface TurnstileDocumentLike {
  createElement: (tagName: 'script') => TurnstileScriptLike
  head: { appendChild: (node: any) => any } | null
}

// Structural views so the resolver never depends on the full page/service
// types (and can be tested with tiny literals).
export interface TurnstilePageSecurityLike {
  security_settings?: { requireCaptcha?: boolean, require_captcha?: boolean } | null
}
export interface TurnstileServiceSecurityLike {
  booking?: { security_settings?: { require_captcha?: boolean } | null } | null
}

/**
 * Whether the resolved settings require a captcha. Mirrors the server's
 * `mergeSecuritySettings(service.securitySettings, page.securitySettings)`:
 * a page override wins over the service default.
 *
 * Shape gotcha: the service setting arrives snake_case
 * (`service.booking.security_settings.require_captcha`) while the PAGE
 * override is emitted as the server's normalised object
 * (`page.securitySettings` → `{ requireCaptcha }`, camelCase). Accept both on
 * the page side; camelCase first because that is what the wire carries today.
 */
export function resolveRequireCaptcha(
  page: TurnstilePageSecurityLike | null | undefined,
  service: TurnstileServiceSecurityLike | null | undefined,
): boolean {
  const pageSettings = page?.security_settings
  const pageValue = pageSettings?.requireCaptcha ?? pageSettings?.require_captcha
  if (typeof pageValue === 'boolean') return pageValue
  return service?.booking?.security_settings?.require_captcha === true
}

/**
 * Submit gating. The token is only demanded when the service requires a
 * captcha AND a site key is configured — with no site key there is no widget
 * to issue a token and the server fails open (warns and allows), so the page
 * must behave exactly as it did before Turnstile existed.
 */
export function canSubmitWithCaptcha(input: {
  requiresCaptcha: boolean
  siteKey: string | null | undefined
  token: string | null | undefined
}): boolean {
  if (!input.requiresCaptcha) return true
  if (!String(input.siteKey ?? '').trim()) return true
  return Boolean(input.token)
}

// One in-flight load per window object (a WeakMap rather than a module-level
// promise so tests with fresh fakes never share state).
const pendingLoads = new WeakMap<object, Promise<TurnstileApi>>()

/**
 * Load Cloudflare's script once (explicit render mode) and resolve with the
 * API. Resolves immediately when `win.turnstile` already exists; concurrent
 * callers share the same promise; a failed load is forgotten so a later call
 * can retry.
 */
export function loadTurnstile(params: {
  doc: TurnstileDocumentLike
  win: TurnstileWindowLike
  timeoutMs?: number
}): Promise<TurnstileApi> {
  const { doc, win } = params
  if (win.turnstile) return Promise.resolve(win.turnstile)
  const pending = pendingLoads.get(win)
  if (pending) return pending

  const promise = new Promise<TurnstileApi>((resolve, reject) => {
    const head = doc.head
    if (!head) {
      reject(new Error('Turnstile needs a document head to load into'))
      return
    }
    const timeoutMs = params.timeoutMs ?? DEFAULT_LOAD_TIMEOUT_MS
    let settled = false
    const timer = setTimeout(() => {
      finish(() => reject(new Error(`Turnstile script timed out after ${timeoutMs}ms`)))
    }, timeoutMs)
    const finish = (outcome: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      outcome()
    }
    const script = doc.createElement('script')
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => {
      finish(() => {
        if (win.turnstile) resolve(win.turnstile)
        else reject(new Error('Turnstile script loaded but the API is not available'))
      })
    }
    script.onerror = () => {
      finish(() => reject(new Error('Turnstile script failed to load')))
    }
    head.appendChild(script)
  })

  pendingLoads.set(win, promise)
  promise.catch(() => {
    // Forget the failed attempt so the next call appends a fresh script.
    if (pendingLoads.get(win) === promise) pendingLoads.delete(win)
  })
  return promise
}

/**
 * Render a widget into `container`; returns the widget id, or null when the
 * API is absent or `render` throws / yields no id (Cloudflare throws on an
 * invalid site key or a container it cannot use — the caller treats every
 * null the same way: no widget, show the load-failure hint).
 */
export function renderTurnstile(
  container: HTMLElement,
  options: {
    siteKey: string
    theme?: TurnstileTheme
    size?: TurnstileSize
    /** Defaults to 'always'. 'interaction-only' shows the widget only when Cloudflare needs a click. */
    appearance?: TurnstileRenderParams['appearance']
    onToken: (token: string) => void
    onExpire?: () => void
    onError?: (code?: string) => void
  },
  win: TurnstileWindowLike,
): string | null {
  const api = win.turnstile
  if (!api) return null
  try {
    const id = api.render(container, {
      'sitekey': options.siteKey,
      'theme': options.theme ?? 'auto',
      'size': options.size ?? 'normal',
      'appearance': options.appearance ?? 'always',
      'callback': options.onToken,
      'expired-callback': () => options.onExpire?.(),
      'error-callback': (code?: string) => options.onError?.(code),
    })
    return typeof id === 'string' && id ? id : null
  }
  catch {
    return null
  }
}

/** Reset a widget (tokens are single-use — call after every failed submit). Never throws. */
export function resetTurnstile(widgetId: string | null | undefined, win: TurnstileWindowLike): void {
  if (!widgetId) return
  try {
    win.turnstile?.reset(widgetId)
  }
  catch {
    // Widget already gone; nothing to reset.
  }
}

/** Remove a widget on unmount. Never throws. */
export function removeTurnstile(widgetId: string | null | undefined, win: TurnstileWindowLike): void {
  if (!widgetId) return
  try {
    win.turnstile?.remove(widgetId)
  }
  catch {
    // Widget already gone; nothing to remove.
  }
}
